import { useEffect } from 'react';
import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ProgressDots } from '@wundo/ui';
import { BackendUnreachable } from '@/components/BackendUnreachable.tsx';
import { OnboardingLanguagePicker } from '@/components/onboarding/OnboardingLanguagePicker.tsx';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { applyUserLocale } from '@/lib/locale.ts';
import { postponeManagerQuestion } from '@/lib/managerQuestionPostponed.ts';
import { onboardingSearch } from '@/lib/onboardingEntry.ts';
import { onboardingPosition } from '@/lib/onboardingSteps.ts';
import { redirectIfPilotEnded } from '@/lib/pilotEnded.ts';
import { takeLinkClick } from '@/lib/linkClick.ts';
import {
  PILOT_CONTEXT_KEY,
  ensurePilotContext,
  fetchPilotContext,
} from '@wundo/client/pilot.ts';
import { requireSession } from '@wundo/client/session.ts';
import { startSessionReplay } from '@/lib/startSessionReplay.ts';
import { m } from '@/paraglide/messages.js';

/*
 * Setting Wundo up: a calendar, the conversations it should take on, and the
 * week that follows from both.
 *
 * A sibling of `_app` rather than a page inside it, so it renders without the
 * rail, the nav and the chat. Someone here has not chosen anything yet, and a
 * shell full of pages that would all be empty is an invitation to wander off
 * mid-setup. The session guard is the same one the shell uses — this is still
 * a signed-in page.
 *
 * Every step is a route of its own, exactly as the settings tabs are. Which
 * step you are on is a fact about the URL and nothing else, so a refresh, a
 * shared link and the browser's Back button all keep it — none of which is true
 * of a step held in a component.
 */
export const Route = createFileRoute('/onboarding')({
  /*
   * The pilot is fetched here rather than on the greeting, because the dots
   * above every step are drawn from it: a wizard that counted five and settled
   * on three a moment later would have moved the user's position under them.
   */
  beforeLoad: async ({ context, location }) => {
    const user = await requireSession(context.queryClient, location.href);
    startSessionReplay(user);
    takeLinkClick(location.href);
    await redirectIfPilotEnded(context.queryClient);

    /* A demo organisation is there to practise, and every step here sets up something else. */
    if (user.demo) {
      throw redirect({ to: '/practice', replace: true });
    }

    await ensurePilotContext(context.queryClient);
  },
  component: OnboardingLayout,
  /* Reached when the guard rethrows: the check failed, rather than answering "no". */
  errorComponent: BackendUnreachable,
  /*
   * A provider that refused sends the browser back with a reason in the URL.
   * Which reason is not something this screen can act on — the remedy is the
   * same either way — so only its presence is read. Declared here so the entry
   * can forward it and the calendar step can report it.
   */
  validateSearch: onboardingSearch,
});

function OnboardingLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });

  /*
   * Signed in, so the browser's language stops being the best guess we have.
   * The shell does this for every page inside it; this one is outside it.
   */
  useEffect(() => {
    applyUserLocale(user?.language);
  }, [user?.language]);

  /*
   * Whichever step the wizard is left from, the app it hands over to should
   * show the user's own week, not a question; the manager question waits for
   * a later visit.
   */
  useEffect(() => {
    postponeManagerQuestion();
  }, []);

  /*
   * Which branch, and where in it. Zero on the entry itself, which is still
   * deciding which step this visit is.
   */
  const { steps, step } = onboardingPosition(pathname, {
    pilot: pilot != null,
    /* A calendar is assumed until the account says otherwise — that is the norm. */
    calendar: user?.calendar_enabled !== false,
  });

  return (
    <main className='mx-auto flex min-h-dvh w-full max-w-240 flex-col px-6 py-10'>
      {/*
        Only the dots, centered: the wizard needs a position, not a masthead.
        The language sits at the row's far end, lifted out of the flow so the
        dots stay centered and the steps keep their height.
      */}
      <div className='relative mb-9 flex justify-center'>
        {step > 0 && (
          <ProgressDots
            total={steps.length}
            current={step}
            label={m.onboarding_progress({ current: step, total: steps.length })}
            stepLabel={(target) => m.onboarding_progress_step({ step: target })}
            /* A finished step is a place, so going back to it is navigation. */
            onSelect={(target) => void navigate({ to: steps[target - 1] })}
          />
        )}
        <OnboardingLanguagePicker className='absolute top-1/2 right-0 -translate-y-1/2' />
      </div>

      <Outlet />
    </main>
  );
}
